import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLoadScript } from "@react-google-maps/api";
import { MapPin } from "lucide-react";

import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/utils";

export interface AddressInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "onSelect"
> {
  value?: string;
  onChange?: (
    value: string,
    placeDetails?: google.maps.places.Place | null,
    subpremise?: string | null,
  ) => void;
  onSelect?: (place: google.maps.places.Place) => void;
  apiKey?: string;
  placeholder?: string;
  className?: string;
  error?: boolean;
  portalContainer?: HTMLElement | null;
  placeTypes?: string[];
  country?: string | null;
}

const libraries: "places"[] = ["places"];

const DROPDOWN_MAX_WIDTH = 420;
const DROPDOWN_MAX_HEIGHT = 240;
const DROPDOWN_MIN_HEIGHT = 120;
const DROPDOWN_GAP = 4;
const VIEWPORT_MARGIN = 8;

export const AddressInput = React.forwardRef<HTMLInputElement, AddressInputProps>(
  (
    {
      value = "",
      onChange,
      onSelect,
      apiKey,
      placeholder = "Search for an address...",
      className,
      error,
      portalContainer,
      placeTypes = ["address"],
      country = "us",
      disabled,
      ...props
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(value);
    const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
    const [selectedPlace, setSelectedPlace] = useState<google.maps.places.Place | null>(null);
    const [inputRect, setInputRect] = useState<DOMRect | null>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [dropdownHost, setDropdownHost] = useState<HTMLElement | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isSelectingRef = useRef(false);

    // Merge refs
    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const { isLoaded, loadError } = useLoadScript({
      googleMapsApiKey: apiKey || import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
      libraries,
    });

    useEffect(() => {
      if (isLoaded && !autocompleteServiceRef.current) {
        autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
      }
    }, [isLoaded]);

    useEffect(() => {
      if (value !== undefined && value !== inputValue) {
        setInputValue(value);
      }
    }, [value]);

    const isOpen = predictions.length > 0 && isFocused;

    useEffect(() => {
      if (!isOpen) {
        setDropdownHost(null);
        return;
      }
      setDropdownHost(
        portalContainer ?? inputRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null,
      );
    }, [isOpen, portalContainer]);

    useEffect(() => {
      if (!isOpen) {
        setInputRect(null);
        return;
      }

      let frame = 0;
      const measure = () => {
        frame = 0;
        if (inputRef.current) {
          setInputRect(inputRef.current.getBoundingClientRect());
        }
      };
      const schedule = () => {
        if (!frame) {
          frame = requestAnimationFrame(measure);
        }
      };

      measure();
      window.addEventListener("scroll", schedule, true);
      window.addEventListener("resize", schedule);

      return () => {
        if (frame) {
          cancelAnimationFrame(frame);
        }
        window.removeEventListener("scroll", schedule, true);
        window.removeEventListener("resize", schedule);
      };
    }, [isOpen, predictions]);

    useEffect(
      () => () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        if (blurTimerRef.current) {
          clearTimeout(blurTimerRef.current);
        }
      },
      [],
    );

    const fetchPredictions = (input: string) => {
      if (!isLoaded || !autocompleteServiceRef.current || input.length < 3) {
        setPredictions([]);
        return;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        autocompleteServiceRef.current?.getPlacePredictions(
          {
            input,
            types: placeTypes,
            ...(country ? { componentRestrictions: { country } } : {}),
          },
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              setPredictions(predictions);
            } else {
              setPredictions([]);
            }
          },
        );
      }, 300);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      setSelectedPlace(null);
      onChange?.(newValue, null, null);

      if (newValue) {
        fetchPredictions(newValue);
      } else {
        setPredictions([]);
      }
    };

    const handleSelectPrediction = async (
      prediction: google.maps.places.AutocompletePrediction,
    ) => {
      if (!isLoaded) return;

      isSelectingRef.current = true;

      try {
        const place = new google.maps.places.Place({
          id: prediction.place_id,
        });

        await place.fetchFields({
          fields: ["addressComponents", "formattedAddress", "location"],
        });

        setSelectedPlace(place);

        const subpremise =
          place.addressComponents?.find((component) => component.types.includes("subpremise"))
            ?.longText || null;

        const formattedAddress = place.formattedAddress || prediction.description;

        setInputValue(formattedAddress);
        setPredictions([]);
        onChange?.(formattedAddress, place, subpremise);
        onSelect?.(place);
      } catch (error) {
        console.error("AddressInput: could not fetch details for the selected address", error);
        setPredictions([]);
      } finally {
        isSelectingRef.current = false;
        inputRef.current?.focus();
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
      }
      // Delay hiding dropdown to allow click on prediction
      blurTimerRef.current = setTimeout(() => {
        blurTimerRef.current = null;
        if (isSelectingRef.current) return;
        setIsFocused(false);
        setPredictions([]);
      }, 200);
      props.onBlur?.(e);
    };

    if (loadError) {
      return (
        <Input className={className} value="Error loading address service" disabled {...props} />
      );
    }

    const getDropdownStyle = (): React.CSSProperties => {
      if (!inputRect) return {};

      const maxWidth = Math.max(
        inputRect.width,
        Math.min(DROPDOWN_MAX_WIDTH, window.innerWidth - inputRect.left - VIEWPORT_MARGIN),
      );

      const spaceBelow = window.innerHeight - inputRect.bottom - DROPDOWN_GAP - VIEWPORT_MARGIN;
      const spaceAbove = inputRect.top - DROPDOWN_GAP - VIEWPORT_MARGIN;
      const dropsUp = spaceBelow < DROPDOWN_MIN_HEIGHT && spaceAbove > spaceBelow;
      const maxHeight = Math.min(
        DROPDOWN_MAX_HEIGHT,
        Math.max(DROPDOWN_MIN_HEIGHT, dropsUp ? spaceAbove : spaceBelow),
      );

      const size = { minWidth: inputRect.width, maxWidth, maxHeight };

      if (dropdownHost) {
        const hostRect = dropdownHost.getBoundingClientRect();
        const left = inputRect.left - hostRect.left;
        return dropsUp
          ? { bottom: hostRect.bottom - inputRect.top + DROPDOWN_GAP, left, ...size }
          : { top: inputRect.bottom - hostRect.top + DROPDOWN_GAP, left, ...size };
      }

      return dropsUp
        ? {
            bottom: window.innerHeight - inputRect.top + DROPDOWN_GAP,
            left: inputRect.left,
            ...size,
          }
        : { top: inputRect.bottom + DROPDOWN_GAP, left: inputRect.left, ...size };
    };

    const dropdownContent = isOpen && inputRect && (
      <div
        className={cn(
          "z-[9999] bg-white border border-stone-200 rounded-md shadow-md overflow-auto p-[5px]",
          dropdownHost ? "absolute" : "fixed",
        )}
        style={getDropdownStyle()}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.preventDefault()}
        data-address-dropdown
      >
        {predictions.map((prediction) => (
          <button
            key={prediction.place_id}
            type="button"
            onClick={() => handleSelectPrediction(prediction)}
            className={cn(
              "flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left",
              "hover:bg-subtle focus:bg-subtle",
              selectedPlace?.formattedAddress === prediction.description && "bg-subtle",
            )}
          >
            <MapPin className="mt-0.5 h-4 w-4 text-tertiary shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium text-primary">
                {prediction.structured_formatting?.main_text ?? prediction.description}
              </span>
              {prediction.structured_formatting?.secondary_text && (
                <span className="block text-2xs text-tertiary">
                  {prediction.structured_formatting.secondary_text}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>
    );

    return (
      <>
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled || !isLoaded}
          placeholder={placeholder}
          className={cn(error && "border-destructive", !isLoaded && "opacity-70", className)}
          {...props}
        />
        {dropdownContent && createPortal(dropdownContent, dropdownHost || document.body)}
      </>
    );
  },
);

AddressInput.displayName = "AddressInput";
